import { apiFetch } from "../api/client";

/**
 * ═══ LES EMAILS QUE LE SITE FAIT PARTIR ═══
 *
 * Une seule porte pour toutes les actions FO : `POST …/emails/v3/send` sur apix, par
 * `actionId`. Ce que l'organisateur a réglé (compte, modèle par langue) se lit dans
 * `Emails`/`EmailLanguages` : c'est la ligne « Email … » de chaque famille du panneau
 * (`panel/ActionEmail.tsx`), et la page « Templates email » du back office.
 *
 * ⚠️ PAS DE MODÈLE, PAS D'ERREUR. Le serveur répond `skipped` quand l'événement n'a rien
 * réglé pour cette action : c'est le cas normal de la plupart des événements, et un
 * envoi qui ne part pas ne doit jamais casser l'inscription qui vient d'aboutir. Les
 * appelants qui ont quelque chose à dire (« badge envoyé ») lisent `sent`.
 *
 * ⚠️ ON N'ENVOIE PAS D'ADRESSE DE RETOUR. Les liens des modèles (`{{var:referer}}?token=…`)
 * ramènent à l'adresse RÉGLÉE pour l'événement (page « Éditeur d'emails » du back-office),
 * jamais à celle d'où l'email a été demandé : le même email part du site comme du
 * back-office, et il doit ramener au site dans les deux cas (décidé le 2026-09-18). Voir
 * `api/src/routes/emails/v3/send`.
 */

/** Les actions FO d'`EmailActionsV2`, par leur identifiant (stable, semé par migration). */
export const EMAIL_ACTION = {
  /** Confirmation d'inscription, sur place. */
  onsite: 1,
  /** Confirmation d'inscription, à distance. */
  remote: 2,
  /** Lien de connexion (auto-connexion, mot de passe oublié). */
  link: 3,
  /** Le badge d'un accompagnant, envoyé depuis l'écran badge. */
  badge: 4,
  /** À la personne DÉSIGNÉE pour représenter quelqu'un. */
  delegate: 5,
  /** À la personne qui se fait représenter. */
  delegating: 6,
  /** Après un refus de participer. */
  decline: 7,
  /** Après un paiement réussi. */
  paid: 8,
  /** À un accompagnant qui vient d'être créé avec une adresse. */
  companionCreated: 10,
  /** À un accompagnant dont le parcours vient d'être terminé. */
  companion: 19,
  /** Le code de connexion. Parti par `/otp/send`, pas par ici. */
  otp: 22,
} as const;
export type EmailActionKind = keyof typeof EMAIL_ACTION;

export type Recipient = { userId: number } | { email: string };

export async function sendActionEmail(
  domainId: number,
  eventId: number,
  action: EmailActionKind,
  to: Recipient,
  options: {
    languageId?: number | null;
    variables?: Record<string, string>;
    /**
     * UNE SEULE FOIS PAR PERSONNE : le serveur écarte qui a déjà reçu cet email d'action
     * (journal `EmailLogsV2`). Le journal s'écrivant en asynchrone, on double d'une trace
     * locale (`sessionStorage`) pour le rechargement immédiat.
     *
     * ⚠️ PLUS POUR LES CONFIRMATIONS (tâche #221) : elles repartent à chaque modification,
     * et c'est leur appelant qui les garde d'un renvoi au rechargement. Voir
     * `emails/confirmation.ts`.
     */
    once?: boolean;
  } = {},
): Promise<{ sent: boolean; already?: boolean }> {
  const mark = options.once
    ? `emailed:${eventId}:${action}:${"userId" in to ? to.userId : to.email}`
    : null;
  if (mark) {
    try {
      if (sessionStorage.getItem(mark)) return { sent: false, already: true };
    } catch {
      /* stockage indisponible : le serveur garde seul la règle */
    }
  }
  const answer = await apiFetch<{
    skipped?: boolean;
    already?: boolean;
  } | null>(`/domains/${domainId}/events/${eventId}/emails/v3/send`, {
    api: "apix",
    method: "POST",
    body: {
      actionId: EMAIL_ACTION[action],
      userId: "userId" in to ? to.userId : null,
      ...("email" in to ? { email: to.email.trim() } : {}),
      ...(options.languageId ? { languageId: options.languageId } : {}),
      ...(options.once ? { once: true } : {}),
      source: "fo",
      ...(options.variables ? { variables: options.variables } : {}),
    },
  });
  if (mark && answer?.skipped !== true) {
    try {
      sessionStorage.setItem(mark, "1");
    } catch {
      /* idem */
    }
  }
  return {
    sent: answer?.skipped !== true && answer?.already !== true,
    ...(answer?.already ? { already: true } : {}),
  };
}
